import {
  clearCache as idbClearCache,
  countCached as idbCount,
  getCachedProfileByDid,
  getCachedProfileByHandle,
  listCached as idbList,
  newestRkey as idbNewest,
  upsertProfile,
  upsertRecords,
} from '../cache/db'

export type Collection =
  | 'app.bsky.feed.post'
  | 'app.bsky.feed.repost'
  | 'app.bsky.feed.like'
  | 'app.bsky.graph.follow'
  | 'app.bsky.graph.block'
  | 'app.bsky.graph.list'
  | 'app.bsky.graph.starterpack'
  | 'app.bsky.feed.generator'

export type CollectionKey =
  | 'posts'
  | 'reposts'
  | 'likes'
  | 'follows'
  | 'blocks'
  | 'lists'
  | 'packs'
  | 'feeds'

export const COLLECTIONS: Record<CollectionKey, Collection> = {
  posts: 'app.bsky.feed.post',
  reposts: 'app.bsky.feed.repost',
  likes: 'app.bsky.feed.like',
  follows: 'app.bsky.graph.follow',
  blocks: 'app.bsky.graph.block',
  lists: 'app.bsky.graph.list',
  packs: 'app.bsky.graph.starterpack',
  feeds: 'app.bsky.feed.generator',
}

export type RecordRow = {
  uri: string
  cid: string | null
  rkey: string
  value: Record<string, unknown>
}

export type FetchPageResult = {
  records: RecordRow[]
  cursor: string | null
  reachedKnown: boolean
}

export type ProfileResult = {
  did: string
  handle: string
  uri: string
  service: string
  profile: {
    displayName?: string
    description?: string
    avatar?: { ref?: { $link?: string } | string }
    banner?: { ref?: { $link?: string } | string }
  } | null
}

export type Session = {
  service: string
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')
const PROFILE_TTL_MS = 24 * 60 * 60 * 1000

function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

function authHeaders(accessJwt?: string): Record<string, string> {
  return accessJwt ? { Authorization: `Bearer ${accessJwt}` } : {}
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const text = await res.text()
    let msg = text
    try {
      msg = JSON.parse(text).error ?? text
    } catch {
      /* keep raw */
    }
    throw new Error(msg || res.statusText)
  }
  return (await res.json()) as T
}

function rkeyFromUri(uri: string): string {
  const parts = uri.split('/')
  return parts[parts.length - 1]
}

const APPVIEW_BASE = 'https://public.api.bsky.app'

export type ActorView = {
  did: string
  handle: string
  displayName?: string
  description?: string
  avatar?: string
  banner?: string
  followsCount?: number
  followersCount?: number
  postsCount?: number
  labels?: { val: string; src?: string }[]
}

export type ActorStatus = 'active' | 'unavailable' | 'loading'

async function appViewFetch<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${APPVIEW_BASE}${path}`, {
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

export const api = {
  health: () => jsonFetch<{ ok: boolean }>(apiUrl('/api/health')),

  login: (input: { service: string; identifier: string; password: string }) =>
    jsonFetch<Session>(apiUrl('/api/auth/login'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  refresh: (input: { service: string; refreshJwt: string }) =>
    jsonFetch<Session>(apiUrl('/api/auth/refresh'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  async profile(handle: string, service?: string): Promise<ProfileResult> {
    // serve from local cache when fresh; refresh otherwise
    const lookupHandle = handle.toLowerCase()
    const cached = lookupHandle.startsWith('did:')
      ? await getCachedProfileByDid(lookupHandle)
      : await getCachedProfileByHandle(lookupHandle)
    if (cached && Date.now() - cached.fetched_at < PROFILE_TTL_MS) {
      return {
        did: cached.did,
        handle: cached.handle,
        uri: cached.uri,
        service: service ?? '',
        profile: cached.profile as ProfileResult['profile'],
      }
    }
    const q = service ? `?service=${encodeURIComponent(service)}` : ''
    const fresh = await jsonFetch<ProfileResult>(
      apiUrl(`/api/profile/${encodeURIComponent(handle)}${q}`),
    )
    await upsertProfile({
      did: fresh.did,
      handle: fresh.handle,
      uri: fresh.uri,
      profile: fresh.profile as Record<string, unknown> | null,
      fetched_at: Date.now(),
    })
    return fresh
  },

  async page(params: {
    handle: string
    collection: Collection
    cursor?: string
    limit?: number
    stopAtRkey?: string
    service?: string
    accessJwt?: string
    signal?: AbortSignal
  }): Promise<FetchPageResult> {
    const sp = new URLSearchParams()
    if (params.cursor) sp.set('cursor', params.cursor)
    if (params.limit) sp.set('limit', String(params.limit))
    if (params.stopAtRkey) sp.set('stopAtRkey', params.stopAtRkey)
    if (params.service) sp.set('service', params.service)
    const qs = sp.toString()
    const result = await jsonFetch<FetchPageResult>(
      apiUrl(
        `/api/records/${encodeURIComponent(params.handle)}/${params.collection}/page${qs ? `?${qs}` : ''}`,
      ),
      { headers: authHeaders(params.accessJwt), signal: params.signal },
    )
    if (result.records.length > 0) {
      await upsertRecords(
        result.records.map((r) => ({
          handle: params.handle.toLowerCase(),
          collection: params.collection,
          rkey: r.rkey || rkeyFromUri(r.uri),
          cid: r.cid,
          uri: r.uri,
          value: r.value,
          fetched_at: Date.now(),
        })),
      )
    }
    return result
  },

  async cache(
    handle: string,
    collection: Collection,
    opts: { offset?: number; limit?: number } = {},
  ) {
    const h = handle.toLowerCase()
    const limit = opts.limit ?? 1000
    const offset = opts.offset ?? 0
    const [records, newestRkey, count] = await Promise.all([
      idbList(h, collection, offset, limit),
      idbNewest(h, collection),
      idbCount(h, collection),
    ])
    return {
      records: records.map((r) => ({
        uri: r.uri,
        cid: r.cid,
        rkey: r.rkey,
        value: r.value,
      })),
      newestRkey,
      count,
    }
  },

  async clearCache(handle: string, collection: Collection) {
    await idbClearCache(handle.toLowerCase(), collection)
    return { ok: true }
  },

  /**
   * AppView lookup for a single actor (DID or handle).
   * Returns null when the AppView doesn't have the account — i.e. it
   * was deleted, deactivated, suspended, or never existed.
   */
  async actor(actor: string): Promise<ActorView | null> {
    return appViewFetch<ActorView>(
      `/xrpc/app.bsky.actor.getProfile?actor=${encodeURIComponent(actor)}`,
    )
  },

  /**
   * AppView paginated list of feed generators an actor has published.
   * Returns hydrated views (description, avatar, subscriber counts).
   */
  async actorFeeds(
    actor: string,
    cursor?: string,
    limit = 50,
  ): Promise<{
    feeds: Array<Record<string, unknown>>
    cursor: string | null
  }> {
    const sp = new URLSearchParams({ actor, limit: String(limit) })
    if (cursor) sp.set('cursor', cursor)
    const data = await appViewFetch<{ feeds?: Array<Record<string, unknown>>; cursor?: string }>(
      `/xrpc/app.bsky.feed.getActorFeeds?${sp.toString()}`,
    )
    return { feeds: data?.feeds ?? [], cursor: data?.cursor ?? null }
  },

  /** AppView paginated list of lists owned by an actor. */
  async actorLists(
    actor: string,
    cursor?: string,
    limit = 50,
  ): Promise<{
    lists: Array<Record<string, unknown>>
    cursor: string | null
  }> {
    const sp = new URLSearchParams({ actor, limit: String(limit) })
    if (cursor) sp.set('cursor', cursor)
    const data = await appViewFetch<{ lists?: Array<Record<string, unknown>>; cursor?: string }>(
      `/xrpc/app.bsky.graph.getLists?${sp.toString()}`,
    )
    return { lists: data?.lists ?? [], cursor: data?.cursor ?? null }
  },

  /** AppView paginated list of starter packs published by an actor. */
  async actorStarterPacks(
    actor: string,
    cursor?: string,
    limit = 50,
  ): Promise<{
    packs: Array<Record<string, unknown>>
    cursor: string | null
  }> {
    const sp = new URLSearchParams({ actor, limit: String(limit) })
    if (cursor) sp.set('cursor', cursor)
    const data = await appViewFetch<{
      starterPacks?: Array<Record<string, unknown>>
      cursor?: string
    }>(`/xrpc/app.bsky.graph.getActorStarterPacks?${sp.toString()}`)
    return { packs: data?.starterPacks ?? [], cursor: data?.cursor ?? null }
  },

  /**
   * Batched AppView lookup. 25 actors per call. Returns a map of did→ActorView
   * for everyone the AppView still serves; missing entries are inactive.
   */
  async actors(actorIdentifiers: string[]): Promise<Map<string, ActorView>> {
    const out = new Map<string, ActorView>()
    if (actorIdentifiers.length === 0) return out
    const unique = Array.from(new Set(actorIdentifiers))
    for (let i = 0; i < unique.length; i += 25) {
      const slice = unique.slice(i, i + 25)
      const sp = new URLSearchParams()
      for (const a of slice) sp.append('actors', a)
      const data = await appViewFetch<{ profiles?: ActorView[] }>(
        `/xrpc/app.bsky.actor.getProfiles?${sp.toString()}`,
      )
      if (data?.profiles) {
        for (const p of data.profiles) out.set(p.did, p)
      }
    }
    return out
  },
}
